
import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { Download, Cpu, Award, CheckCircle, ArrowRight, ShieldCheck, RefreshCcw } from 'lucide-react';
import CertificatePreview from './components/CertificatePreview';
import CertificateForm from './components/CertificateForm';
import { UserData } from './types';

const App: React.FC = () => {
  const [userData, setUserData] = useState<UserData>({
    fullName: '',
    email: '',
    submissionScreenshot: null
  });
  const [appState, setAppState] = useState<'entry' | 'generating' | 'revealed'>('entry');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const certificateRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (base64: string | null) => {
    setUserData(prev => ({ ...prev, submissionScreenshot: base64 }));
  };

  const startGeneration = () => {
    setAppState('generating');
    setGenerationProgress(0);
  };

  useEffect(() => {
    if (appState === 'generating') {
      const interval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => setAppState('revealed'), 600);
            return 100;
          }
          return prev + 1.5;
        });
      }, 25);
      return () => clearInterval(interval);
    }
  }, [appState]);

  const handleDownload = async () => {
    if (!certificateRef.current) return;
    setIsDownloading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const canvas = await html2canvas(certificateRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#0b1622',
        logging: false,
      });
      const image = canvas.toDataURL("image/png", 1.0);
      const link = document.createElement('a');
      link.download = `AI_Bharat_26_Cert_${userData.fullName.replace(/\s+/g, '_') || 'Participant'}.png`;
      link.href = image;
      link.click();
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const reset = () => {
    setAppState('entry');
    setUserData({ fullName: '', email: '', submissionScreenshot: null });
  };

  const isFormValid = userData.fullName.trim() !== '' && 
                      userData.email.trim() !== '' && 
                      userData.submissionScreenshot !== null;

  return (
    <div className="min-h-screen bg-tech-mesh text-white relative flex flex-col items-center justify-center p-4 overflow-hidden">
      {/* Dynamic Glow Layers */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#00d4ff]/5 to-transparent pointer-events-none"></div>
      <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] bg-[#00d4ff]/10 rounded-full blur-[140px] animate-pulse"></div>
      <div className="absolute -bottom-[20%] -right-[10%] w-[60%] h-[60%] bg-[#001f5c]/40 rounded-full blur-[140px]"></div>

      <div className="w-full max-w-5xl z-10">
        
        {appState === 'entry' && (
          <div className="flex flex-col items-center animate-in fade-in zoom-in">
            <header className="text-center mb-10">
              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-md">
                <ShieldCheck className="w-4 h-4 text-[#00d4ff]" />
                <span className="text-[10px] font-futuristic tracking-[0.4em] uppercase text-cyan-400">Credential Verification v3.0</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-futuristic font-black tracking-tighter mb-4">
                AI FOR BHARAT <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d4ff] via-white to-[#00d4ff] bg-[length:200%_auto] animate-shimmer">2026</span>
              </h1>
              <p className="text-white/30 font-futuristic text-[11px] tracking-[0.6em] uppercase">Securing the Future of Innovation</p>
            </header>

            <div className="w-full max-w-md bg-white/[0.03] backdrop-blur-3xl border border-white/10 p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-[#00d4ff]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-[#00d4ff] to-[#001f5c] text-white p-4 rounded-2xl shadow-xl">
                <Award size={28} />
              </div>
              
              <CertificateForm 
                userData={userData} 
                onChange={handleInputChange} 
                onImageUpload={handleImageUpload}
              />

              <button
                onClick={startGeneration}
                disabled={!isFormValid}
                className="mt-10 w-full group relative overflow-hidden flex items-center justify-center gap-3 px-8 py-5 rounded-2xl font-futuristic font-extrabold text-sm tracking-widest transition-all disabled:opacity-20 disabled:cursor-not-allowed bg-white text-[#000814] hover:bg-[#00d4ff] hover:text-[#000814] hover:shadow-[0_0_30px_rgba(0,212,255,0.4)]"
              >
                ACCESS CREDENTIALS
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </button>
            </div>
          </div>
        )}

        {appState === 'generating' && (
          <div className="flex flex-col items-center justify-center text-center py-20 animate-in fade-in">
            <div className="relative mb-12">
              <div className="absolute inset-0 bg-cyan-400 blur-3xl opacity-20 animate-pulse"></div>
              <Cpu className="w-24 h-24 text-[#00d4ff] animate-spin relative z-10" style={{ animationDuration: '4s' }} />
            </div>
            <h2 className="text-3xl font-futuristic font-bold tracking-[0.3em] mb-6">PROCESSING SYNC</h2>
            <div className="w-full max-w-sm h-1.5 bg-white/5 rounded-full overflow-hidden mb-6">
              <div 
                className="h-full bg-gradient-to-r from-[#00d4ff] to-white transition-all duration-300 shadow-[0_0_20px_#00d4ff]" 
                style={{ width: `${generationProgress}%` }}
              ></div>
            </div>
            <div className="font-mono text-[10px] text-cyan-400/50 uppercase tracking-widest space-y-1">
              <p>{generationProgress < 30 ? "> Initializing Neural Handshake..." : 
                  generationProgress < 60 ? "> Verifying Bharat Hackathon Ledger..." : 
                  generationProgress < 90 ? "> Building Cryptographic Certificate..." : 
                  "> Finalizing Output..."}</p>
              <p className="opacity-40">Node: mc-meta-node-0912</p>
            </div>
          </div>
        )}

        {appState === 'revealed' && (
          <div className="flex flex-col items-center animate-in fade-in slide-in-from-bottom-10">
            <div className="flex items-center gap-6 mb-10">
              <button 
                onClick={reset}
                className="group flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
              >
                <RefreshCcw size={16} className="text-white/40 group-hover:rotate-180 transition-transform duration-500" />
                <span className="text-[10px] font-futuristic tracking-widest opacity-40 group-hover:opacity-100 uppercase">New Entry</span>
              </button>
              <div className="h-px w-12 bg-white/10"></div>
              <h2 className="text-2xl font-futuristic font-black text-white tracking-[0.3em]">VALIDATED</h2>
            </div>

            <div className="w-full relative group max-w-4xl">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#00d4ff]/20 to-transparent blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
              
              <div className="overflow-x-auto pb-8 scrollbar-hide flex justify-center">
                <div className="min-w-[850px] lg:min-w-0 lg:w-full">
                  <div ref={certificateRef} className="shadow-[0_50px_120px_rgba(0,0,0,0.9)] rounded-sm overflow-hidden scale-[0.98] hover:scale-100 transition-transform duration-700">
                    <CertificatePreview name={userData.fullName} />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-col items-center gap-6 w-full max-w-md">
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="w-full flex items-center justify-center gap-4 px-10 py-6 rounded-[2rem] font-futuristic font-black text-sm tracking-[0.2em] bg-[#00d4ff] text-[#001b44] hover:bg-white hover:shadow-[0_0_50px_rgba(255,255,255,0.3)] hover:-translate-y-1 transition-all active:scale-95"
              >
                {isDownloading ? (
                  <div className="w-6 h-6 border-3 border-[#001b44] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Download className="w-6 h-6" />
                )}
                {isDownloading ? "GENERATING FILE..." : "DOWNLOAD HIGH-RES PNG"}
              </button>
              <p className="text-white/20 text-[9px] font-futuristic uppercase tracking-[0.5em] text-center">
                Digital Signature Authenticated by Meerut Coders & Meta Infinium
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
